import { Component, computed, EnvironmentInjector, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
    FormRowComponent,
    InstructionsComponent,
    InstructionsWithFieldsContainerComponent,
    TypeaheadSearchComponent,
} from '@r1-compass-library/components';
import { DataMartField, DataMartTable } from '@r1-compass-library/models';
import { rxEffect } from 'ngxtension/rx-effect';
import { toObservable } from '@angular/core/rxjs-interop';
import { DivisionBasicSettingsFormService } from '../division-basic-settings-form.service';

@Component({
    selector: 'r1-compass-app-datamart',
    imports: [
        InstructionsWithFieldsContainerComponent,
        InstructionsComponent,
        FormRowComponent,
        TypeaheadSearchComponent,
        MatSlideToggleModule,
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule,
    ],
    template: `
        <r1-compass-lib-instructions-with-fields-container>
            <r1-compass-lib-instructions headerText="Data Mart" instructions>
                Search for and select the Data Mart table to use for querying. Then, select the field which will be used
                for the initial Template Criteria when querying this table.*
            </r1-compass-lib-instructions>
            <form formArea [formGroup]="formService.divisionBasicSettingsForm">
                <r1-compass-lib-form-row name="Data Mart Table *">
                    <r1-compass-lib-typeahead-search
                        [valueFormControl]="formService.divisionBasicSettingsForm.controls.dataMartTable"
                        [displayFormControl]="dataMartTableDisplayControl"
                        [resultDisplayPropertyPath]="'displayName'"
                        [resultValuePropertyPath]="'name'"
                        [disabled]="false"
                        [searchResults]="dataMartTableResults()"
                        (search)="dataMartTableSearchState.set($event)"
                        (resultSelection)="dataMartTableSearchState.set('')" />
                </r1-compass-lib-form-row>
                <r1-compass-lib-form-row name="Required Field *">
                    <mat-form-field>
                        <mat-label>Required Field</mat-label>
                        <mat-select formControlName="dataMartRequiredField">
                            @for (field of requiredDatamartFields(); track $index) {
                                <mat-option [value]="field.name">{{ field.displayName }} ({{ field.name }})</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </r1-compass-lib-form-row>
                <r1-compass-lib-form-row name="Allow Segmentation Query Override">
                    <mat-slide-toggle
                        formControlName="allowOverrideQueries"
                        [checked]="formService.divisionBasicSettingsForm.controls.allowOverrideQueries" />
                </r1-compass-lib-form-row>
            </form>
        </r1-compass-lib-instructions-with-fields-container>
    `,
})
export class DatamartComponent {
    readonly #injector = inject(EnvironmentInjector);
    protected readonly formService = inject(DivisionBasicSettingsFormService);

    private formState = this.formService.divisionBasicSettingsFormState;

    protected dataMartTableSearchState = signal('');
    protected dataMartTableDisplayControl = new FormControl('');

    protected dataMartTableResults = computed<DataMartTable[]>(() =>
        this.dataMartTableSearchState() !== ''
            ? this.formState
                  .dataMartTableOptions()
                  .filter(tableOption =>
                      tableOption.displayName.toLowerCase().includes(this.dataMartTableSearchState().toLowerCase())
                  )
            : []
    );

    protected requiredDatamartFields = computed<DataMartField[]>(
        () =>
            this.formState.dataMartFields().sort(function (a, b) {
                return a.displayName.localeCompare(b.displayName!);
            }) ?? []
    );

    constructor() {
        // Set typeaheads/display forms
        rxEffect(
            toObservable(this.formState.dataMartTableOptions, { injector: this.#injector }),
            dataMartTableOptions => {
                dataMartTableOptions.find(dataMartOption => {
                    if (dataMartOption.name === this.formState.value().dataMartTable) {
                        this.dataMartTableDisplayControl.setValue(dataMartOption.displayName);
                        this.dataMartTableSearchState.set(dataMartOption.displayName);
                    }
                });
            }
        );
    }
}
